import { Box } from "@mui/material";
import styles from "./styles.module.css";
import {
  BlackCheckIcon,
  GreenCheck,
  ItemCloseIcon,
  UploadAttachment,
} from "../../../assets/icons/icon";
import { useContext, useRef, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import { ParagraphText } from "../../../Libraries";
import UBDateSelect from "../../../Libraries/UBDateSelect";
import { ValidateUploadedFile } from "../../../utils";
import { useSnackbar } from "../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../config/firebase";

const FileAttachment = ({
  filesPath = [],
  setFilesPath,
  id = "",
  handleLogEvent = () => {},
  handleRemoveLogEvent = () => {},
  showDate = false,
  selectedDate,
  setSelectedDate,
  handleDateLogEvent = () => {},
  datePlaceholder,
  isEditMode = false,
  variant = "default",
}) => {
  const isBillUpload = variant === "billUpload";
  const [I18n] = useContext(LanguageContext);
  const [files, setFiles] = useState(filesPath);

  const fileInputRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  const pickFilesFromGallery = (selectedFiles) => {
   
    if (selectedFiles?.length > 10) {
      showSnackbar({
        message: "Maximum of 10 files allowed",
        severity: "error",
      });
      return;
    }

    for (let file of selectedFiles) {
      const valid = ValidateUploadedFile({
        file,
        I18n,
        allowedTypes: [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // Word Docx
          "text/plain", // .txt
          "application/vnd.ms-excel", // .xls
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        ],
      });
      if (!valid.valid) {
        showSnackbar({ message: valid.message, severity: "error" });
        return;
      }
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        triggerLogEvent(analytics, "file_attached", {
          file_type: file.type,
        });
        handleLogEvent();
        setFiles((f) => {
          setFilesPath([
            ...f,
            {
              name: file.name,
              file: reader.result,
              type: file.type,
            },
          ]);
          return [
            ...f,
            {
              name: file.name,
              file: reader.result,
              type: file.type,
            },
          ];
        });
      };
    }
  };

  const handleUploadNewFile = () => {
    if (filesPath?.length >= 10) {
      showSnackbar({
        message: "Maximum of 10 files allowed",
        severity: "error",
      });
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (index) => {
    handleRemoveLogEvent();
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
    setFilesPath(updatedFiles);
  };

  const handleDate = (date) => {
    if (date) {
      let j_date = new Date(date);
      j_date.setHours(0, 0, 0, 0);

      setSelectedDate(new Date(j_date));
    } else {
      setSelectedDate(new Date());
    }
  };

  const uploadBox = (
    <Box
      className={`${styles.inputWrapper} ${
        isBillUpload ? styles.billUploadInputWrapper : ""
      } FileAttachmentInputWrapper`}
      id={`${id}_addFiles`}
      tabIndex={0}
      role="button"
      onClick={handleUploadNewFile}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleUploadNewFile();
        }
      }}
    >
      <Box
        className={`${styles.uploadAttachmentBox} ${
          isBillUpload ? styles.billUploadAttachmentBox : ""
        }`}
        display="flex"
        alignItems="center"
      >
        <UploadAttachment />{" "}
        <ParagraphText
          className={`${
            isBillUpload ? `fontSize14 ${styles.billUploadText}` : "fontSize12 barclayBlueColor"
          }`}
        >
          {isBillUpload ? I18n.upload_bill : I18n.upload_attachment}
        </ParagraphText>
      </Box>
      <input
        ref={fileInputRef}
        id="file_attachment_input"
        name="files"
        type="file"
        multiple
        onChange={(e) => {
          pickFilesFromGallery(e.target.files);
          e.target.value = "";
        }}
      />
    </Box>
  );

  const fileListContent =
    files.length > 0 ? (
      <Box className={styles.fileList}>
        {files.map((file, index) => (
          <Box
            key={index}
            className={`${styles.fileItem} ${
              isBillUpload ? styles.billUploadFileItem : ""
            }`}
          >
            <ParagraphText className={styles.fileName}>
              {file.name || "Unknown file"}
            </ParagraphText>
            <ParagraphText className={styles.fileItemActions}>
              <span className={styles.tickIcon}>
                {isBillUpload ? (
                  <GreenCheck width={14} height={14} />
                ) : (
                  <BlackCheckIcon width={14} height={14} />
                )}
              </span> 
              <span className={styles.actionSeperator}></span>
              <span
                id={`${id}_deleteFile_${index}`}
                className={styles.deleteIcon}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    handleRemoveFile(index);
                  }
                }}
                onClick={() => handleRemoveFile(index)}
              >
                <ItemCloseIcon />
              </span>
            </ParagraphText>
          </Box>
        ))}
      </Box>
    ) : null;

  return (
    <Box
      className={`${styles.root} ${showDate ? styles.rootWithDate : ""} ${
        isBillUpload ? styles.billUploadRoot : ""
      } FileAttachmentInline`}
    >
      {showDate ? (
        <>
          <Box className={styles.attachmentAndDateRow}>
            <Box className={styles.attachmentSection}>{uploadBox}</Box>
            <Box className={styles.dateSection}>
              <UBDateSelect
                id={`${id}_date`}
                selectedDate={selectedDate}
                setSelectedDate={handleDate}
                title={null}
                placeholder={datePlaceholder || I18n.select_date}
                isCalendericon
                handleLogEvent={isEditMode ? () => {} : handleDateLogEvent}
              />
            </Box>
          </Box>
          {fileListContent}
        </>
      ) : (
        <>
          {uploadBox}
          {fileListContent}
        </>
      )}
    </Box>
  );
};

export default FileAttachment;
